/**
 * DocumentCanvas - the studio's hero: a paper-like rendering of the SOW that the
 * admin edits directly. Each logical section is a selectable region. The selected
 * FIXED (static) section mounts an inline rich-text editor in place; every other
 * section (and unselected ones) renders read-only via `DocContractView`, with
 * AI / data / signature / TOC parts shown as labeled slot blocks.
 *
 * Selection granularity is the logical section (the only stable unit the contract
 * exposes); finer edits happen inside the section's editor.
 */

import type { DocumentChild } from '@protoss/doc';
import { tiptapToDocumentChildren } from '@protoss/doc';
import { RichTextEditor, type Editor } from '@protoss/ui';
import * as React from 'react';
import { DocContractView, type PreviewMode, type PreviewSample } from '../../../../components/doc';
import { parseContentToTiptap } from '../../../../lib/tiptap';
import {
  sectionBodyToMarkdown,
  sectionLabel,
  withBody,
  type LogicalSection,
} from '../../lib/sowContract';

export interface DocumentCanvasProps {
  sections: LogicalSection[];
  sample: PreviewSample;
  headings: Array<{ text: string; level: number }>;
  previewMode: PreviewMode;
  selectedId: string | null;
  canEdit: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, text: string) => void;
  onChangeSection: (next: LogicalSection) => void;
  /** Receives the active inline editor (and null on deselect) for token insertion. */
  onActiveEditor?: (editor: Editor | null) => void;
}

export function DocumentCanvas({
  sections,
  sample,
  headings,
  previewMode,
  selectedId,
  canEdit,
  onSelect,
  onRename,
  onChangeSection,
  onActiveEditor,
}: DocumentCanvasProps) {
  return (
    <div className="sow-studio-canvas">
      <div className="sow-studio-canvas__paper" role="document">
        {sections.map((section) => (
          <SectionBlock
            key={section.id}
            section={section}
            sample={sample}
            headings={headings}
            previewMode={previewMode}
            selected={section.id === selectedId}
            editable={canEdit}
            onSelect={onSelect}
            onRename={onRename}
            onChangeSection={onChangeSection}
            onActiveEditor={onActiveEditor}
          />
        ))}
      </div>
    </div>
  );
}

interface SectionBlockProps {
  section: LogicalSection;
  sample: PreviewSample;
  headings: Array<{ text: string; level: number }>;
  previewMode: PreviewMode;
  selected: boolean;
  editable: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, text: string) => void;
  onChangeSection: (next: LogicalSection) => void;
  onActiveEditor?: (editor: Editor | null) => void;
}

function SectionBlock({
  section,
  sample,
  headings,
  previewMode,
  selected,
  editable,
  onSelect,
  onRename,
  onChangeSection,
  onActiveEditor,
}: SectionBlockProps) {
  // Inline prose editing is offered only for pure FIXED sections; AI/data/
  // signature/TOC/mixed render their preview and are configured in the inspector.
  const inlineEditable = selected && editable && section.contentType === 'static';

  return (
    <section
      data-sow-section-id={section.id}
      className={`sow-studio-section${selected ? ' sow-studio-section--selected' : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={sectionLabel(section)}
      onClick={() => onSelect(section.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(section.id);
        }
      }}
    >
      {inlineEditable ? (
        <InlineSectionEditor
          section={section}
          onRename={onRename}
          onChangeSection={onChangeSection}
          onActiveEditor={onActiveEditor}
        />
      ) : (
        <DocContractView
          content={sectionContent(section)}
          mode={previewMode}
          sample={sample}
          headings={headings}
        />
      )}
    </section>
  );
}

/** Heading paragraph (if any) + body, the content `DocContractView` renders. */
function sectionContent(section: LogicalSection): DocumentChild[] {
  return section.heading ? [section.heading, ...section.body] : section.body;
}

function InlineSectionEditor({
  section,
  onRename,
  onChangeSection,
  onActiveEditor,
}: {
  section: LogicalSection;
  onRename: (id: string, text: string) => void;
  onChangeSection: (next: LogicalSection) => void;
  onActiveEditor?: (editor: Editor | null) => void;
}) {
  // Seed once per section so typing never resets the cursor (per-section editor model).
  const seed = React.useMemo(
    () => parseContentToTiptap(sectionBodyToMarkdown(section)),
    [section.id]
  );
  return (
    <div className="sow-studio-section__editor">
      {!section.isPreamble ? (
        <input
          className="sow-studio-section__heading-input"
          value={section.headingText}
          placeholder="Section heading"
          aria-label="Section heading"
          onChange={(e) => onRename(section.id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : null}
      <div onClick={(e) => e.stopPropagation()}>
        <RichTextEditor
          content={seed}
          tokens
          onEditorReady={onActiveEditor}
          onUpdate={(json) => onChangeSection(withBody(section, tiptapToDocumentChildren(json)))}
        />
      </div>
    </div>
  );
}
